import { AnalyticsRelationshipRouteUnavailableError } from "@redotech/redo-analytics-custom-analytics/semantic-catalog/v1/relationships/analytics-relationship-route-pins";
import { CUSTOM_ANALYTICS_QUERY_SORT_LIMIT } from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/shared/custom-analytics-query-limits";
import { SortDirectionSchema } from "@redotech/redo-views-common/table";
import * as z from "zod";

export const CUSTOM_ANALYTICS_REPORT_TITLE_MAX_LENGTH = 80;
export const CUSTOM_ANALYTICS_REPORT_COLUMN_LABEL_MAX_LENGTH = 80;
export const CUSTOM_ANALYTICS_REPORT_INTERNAL_ERROR_MESSAGE =
  "The report could not be prepared.";
export const CUSTOM_ANALYTICS_REPORT_DEFAULT_PERCENT_DECIMAL_PLACES = 2;
export const CUSTOM_ANALYTICS_REPORT_SOFT_VISIBLE_COLUMN_LIMIT = 15;
export const CUSTOM_ANALYTICS_REPORT_STORE_TIMEZONE = "store_setting";

const MACHINE_GENERATED_COLUMN_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const CAMEL_CASE_COLUMN_NAME_PATTERN = /[a-z][A-Z]/;

export const CustomAnalyticsReportFailureCode = {
  GenerationFailed: "generation_failed",
  QueryCompilationFailed: "query_compilation_failed",
  QueryValidationFailed: "query_validation_failed",
  RelationshipRouteUnavailable: "relationship_route_unavailable",
  MetadataFailed: "metadata_failed",
  PersistenceFailed: "persistence_failed",
  InternalError: "internal_error",
} as const;

export const CustomAnalyticsReportFailureReason = {
  QueryUnavailable: "query_unavailable",
} as const;

export const CustomAnalyticsReportFailureReasonSchema = z.enum([
  CustomAnalyticsReportFailureReason.QueryUnavailable,
]);

export type CustomAnalyticsReportFailureReason = z.infer<
  typeof CustomAnalyticsReportFailureReasonSchema
>;

export const CustomAnalyticsReportFailureCodeSchema = z.enum([
  CustomAnalyticsReportFailureCode.GenerationFailed,
  CustomAnalyticsReportFailureCode.QueryCompilationFailed,
  CustomAnalyticsReportFailureCode.QueryValidationFailed,
  CustomAnalyticsReportFailureCode.RelationshipRouteUnavailable,
  CustomAnalyticsReportFailureCode.MetadataFailed,
  CustomAnalyticsReportFailureCode.PersistenceFailed,
  CustomAnalyticsReportFailureCode.InternalError,
]);

export type CustomAnalyticsReportFailureCode = z.infer<
  typeof CustomAnalyticsReportFailureCodeSchema
>;

export const CustomAnalyticsReportTitleSchema = z
  .string()
  .trim()
  .min(1)
  .max(CUSTOM_ANALYTICS_REPORT_TITLE_MAX_LENGTH);

export const CustomAnalyticsReportColumnKindSchema = z.enum([
  "text",
  "number",
  "integer",
  "currency",
  "percent",
  "date",
  "datetime",
  "boolean",
  "json",
]);

export type CustomAnalyticsReportColumnKind = z.infer<
  typeof CustomAnalyticsReportColumnKindSchema
>;

export const CustomAnalyticsReportColumnFormatSchema = z.object({
  decimalPlaces: z.number().int().min(0).max(6).nullable(),
});

export type CustomAnalyticsReportColumnFormat = z.infer<
  typeof CustomAnalyticsReportColumnFormatSchema
>;

export const CustomAnalyticsReportColumnHelpFilterSchema = z.object({
  label: z.string().trim().min(1),
  description: z.string().trim().min(1),
});

export type CustomAnalyticsReportColumnHelpFilter = z.infer<
  typeof CustomAnalyticsReportColumnHelpFilterSchema
>;

export const CustomAnalyticsReportColumnHelpSchema = z.object({
  description: z.string().trim().min(1).optional(),
  calculation: z.string().trim().min(1).optional(),
  caveat: z.string().trim().min(1).optional(),
  filters: z.array(CustomAnalyticsReportColumnHelpFilterSchema).optional(),
  relatedThrough: z.string().trim().min(1).optional(),
  source: z.enum(["catalog", "query_plan"]),
});

export type CustomAnalyticsReportColumnHelp = z.infer<
  typeof CustomAnalyticsReportColumnHelpSchema
>;

const CustomAnalyticsReportColumnBaseSchema = z.object({
  key: z.string(),
  sourceName: z.string(),
  label: z.string(),
  sqlType: z.string(),
  order: z.number().int().nonnegative(),
  align: z.enum(["left", "right"]).default("left"),
  help: CustomAnalyticsReportColumnHelpSchema.optional(),
});

const CustomAnalyticsReportFormattedColumnSchema =
  CustomAnalyticsReportColumnBaseSchema.extend({
    kind: z.enum(["number", "percent"]),
    format: CustomAnalyticsReportColumnFormatSchema.nullable().optional(),
  });

const CustomAnalyticsReportUnformattedColumnSchema =
  CustomAnalyticsReportColumnBaseSchema.extend({
    kind: z.enum([
      "text",
      "integer",
      "currency",
      "date",
      "datetime",
      "boolean",
      "json",
    ]),
    format: z.null().optional(),
  });

export const CustomAnalyticsReportColumnSchema = z.discriminatedUnion("kind", [
  CustomAnalyticsReportFormattedColumnSchema,
  CustomAnalyticsReportUnformattedColumnSchema,
]);

export type CustomAnalyticsReportColumn = z.infer<
  typeof CustomAnalyticsReportColumnSchema
>;

export const CustomAnalyticsReportColumnsSchema = z.array(
  CustomAnalyticsReportColumnSchema,
);

export const CustomAnalyticsReportSortSchema = z.object({
  key: z.string(),
  direction: SortDirectionSchema,
});

export type CustomAnalyticsReportSort = z.infer<
  typeof CustomAnalyticsReportSortSchema
>;

export const CustomAnalyticsReportSortsSchema = z
  .array(CustomAnalyticsReportSortSchema)
  .max(CUSTOM_ANALYTICS_QUERY_SORT_LIMIT);

export function isCustomAnalyticsReportColumnSortable(
  column: CustomAnalyticsReportColumn,
): boolean {
  return column.kind !== "json";
}

const CustomAnalyticsReportColumnPresentationBaseSchema = z.object({
  sourceName: z
    .string()
    .describe("Exact final output alias from the query plan select list."),
  label: z
    .string()
    .nullable()
    .describe("Merchant-facing table header. Use null to accept the default."),
  labelManuallyEdited: z.boolean().optional(),
});

const CustomAnalyticsReportNoFormatColumnPresentationSchema =
  CustomAnalyticsReportColumnPresentationBaseSchema.extend({
    kind: z.enum([
      "text",
      "integer",
      "currency",
      "date",
      "datetime",
      "boolean",
      "json",
    ]),
    format: z.null(),
  });

const CustomAnalyticsReportDecimalColumnPresentationSchema =
  CustomAnalyticsReportColumnPresentationBaseSchema.extend({
    kind: z.enum(["number", "percent"]),
    format: CustomAnalyticsReportColumnFormatSchema.nullable(),
  });

export const CustomAnalyticsReportColumnPresentationSchema =
  z.discriminatedUnion("kind", [
    CustomAnalyticsReportNoFormatColumnPresentationSchema,
    CustomAnalyticsReportDecimalColumnPresentationSchema,
  ]);

export type CustomAnalyticsReportColumnPresentation = z.infer<
  typeof CustomAnalyticsReportColumnPresentationSchema
>;

/** Array order is display order and stays aligned with semantic query columns. */
export const CustomAnalyticsReportColumnPresentationsSchema = z.array(
  CustomAnalyticsReportColumnPresentationSchema,
);

export interface CustomAnalyticsReportSourceColumn {
  name: string;
  type: string;
  scale?: number | null;
  precision?: number | null;
}

export interface BuildCustomAnalyticsReportColumnsOptions {
  columnPresentation?:
    | readonly CustomAnalyticsReportColumnPresentation[]
    | null;
  columnHelp?: readonly CustomAnalyticsReportColumnHelpInput[] | null;
  /** Label and kind for a column the stored presentation does not cover. */
  columnDefaults?: readonly CustomAnalyticsReportColumnDefault[] | null;
}

export interface CustomAnalyticsReportColumnDefault {
  sourceName: string;
  label?: string;
  kind?: CustomAnalyticsReportColumnKind;
}

export interface CustomAnalyticsReportColumnHelpInput {
  sourceName: string;
  help: CustomAnalyticsReportColumnHelp;
}

// Match only the final space between a Snowflake time and numeric TZ offset so
// timestamp normalization does not alter ordinary date/time spaces.
const SNOWFLAKE_TIMESTAMP_TZ_OFFSET_SEPARATOR =
  /(\d{2}:\d{2}:\d{2}(?:\.\d+)?) ([+-]\d{4})$/;
// Split quoted camelCase aliases into words before deriving labels and row keys.
const CAMEL_CASE_WORD_BOUNDARY = /([a-z0-9])([A-Z])/g;
// Split labels on any non-alphanumeric run because SQL aliases can contain
// spaces, underscores, punctuation, or other separators.
const COLUMN_WORD_SEPARATOR = /[^a-zA-Z0-9]+/;
const NUMERIC_COLUMN_KINDS = new Set<CustomAnalyticsReportColumnKind>([
  "number",
  "integer",
  "currency",
  "percent",
]);
const ASCII_UPPERCASE_A_CHAR_CODE = "A".charCodeAt(0);
const ASCII_UPPERCASE_Z_CHAR_CODE = "Z".charCodeAt(0);
const ASCII_LOWERCASE_A_CHAR_CODE = "a".charCodeAt(0);
const ASCII_LOWERCASE_Z_CHAR_CODE = "z".charCodeAt(0);
const CURRENCY_COLUMN_NAME_WORDS = new Set([
  "amount",
  "price",
  "refund",
  "revenue",
  "cost",
  "tax",
  "subtotal",
  "total",
  "value",
  "sales",
  "gmv",
]);
const PERCENT_COLUMN_NAME_WORDS = new Set([
  "rate",
  "percent",
  "percentage",
  "pct",
  "ratio",
]);
const INTEGER_COLUMN_NAME_WORDS = new Set([
  "count",
  "quantity",
  "qty",
  "number",
  "orders",
  "returns",
  "items",
  "units",
]);
/*
 * These patterns intentionally match arbitrary framework/runtime error text.
 * They keep stack traces, module paths, and local filesystem details out of
 * merchant-facing custom report errors while preserving safe failure codes.
 */
const CUSTOM_ANALYTICS_REPORT_INTERNAL_ERROR_PATTERNS = [
  /\bActivity task failed\b/i,
  /\bApplicationFailure\b/i,
  /\bCannot find module\b/i,
  /\bRequire stack\b/i,
  /\bWorkflow execution failed\b/i,
  /\bat\s+\S+\s+\(/,
  /\bbazel-out\b/i,
  /\blocal\.runfiles\b/i,
  /\bnode_modules\b/i,
  /\/Users\/[^/\s]+/,
  /\\Users\\[^\\\s]+/,
];

const CUSTOM_ANALYTICS_REPORT_FAILURE_MESSAGES: Record<
  CustomAnalyticsReportFailureCode,
  string
> = {
  [CustomAnalyticsReportFailureCode.GenerationFailed]:
    "The report could not be generated.",
  [CustomAnalyticsReportFailureCode.QueryCompilationFailed]:
    "The report definition could not be prepared.",
  [CustomAnalyticsReportFailureCode.QueryValidationFailed]:
    "The report definition could not be validated.",
  [CustomAnalyticsReportFailureCode.RelationshipRouteUnavailable]:
    "This report asked for a relationship meaning that is not available. Ask again naming the meaning you want.",
  [CustomAnalyticsReportFailureCode.MetadataFailed]:
    "The report columns could not be prepared.",
  [CustomAnalyticsReportFailureCode.PersistenceFailed]:
    "The report could not be saved.",
  [CustomAnalyticsReportFailureCode.InternalError]:
    CUSTOM_ANALYTICS_REPORT_INTERNAL_ERROR_MESSAGE,
};

/**
 * A route refusal is user-correctable, so it must not land on the generic
 * validation message the merchant cannot act on; anything else keeps `fallback`.
 */
export function customAnalyticsReportValidationFailureCode(
  error: unknown,
  fallback: CustomAnalyticsReportFailureCode = CustomAnalyticsReportFailureCode.QueryValidationFailed,
): CustomAnalyticsReportFailureCode {
  return error instanceof AnalyticsRelationshipRouteUnavailableError
    ? CustomAnalyticsReportFailureCode.RelationshipRouteUnavailable
    : fallback;
}

export function normalizeCustomAnalyticsReportTimestampValue(
  dateString: string,
): string {
  /*
   * Snowflake TIMESTAMP_TZ values can arrive from the JS driver like:
   *   "2025-10-08 07:52:25.364 -0600"
   *
   * Safari treats the space before the numeric timezone offset as invalid when
   * passed to `new Date(...)`; Chrome is more permissive. Removing only that
   * final separator gives:
   *   "2025-10-08 07:52:25.364-0600"
   *
   * The regex is anchored to the end of the string and requires a time plus
   * numeric timezone offset so we do not change ordinary spaces in the date
   * portion of the timestamp.
   */
  return dateString.replace(SNOWFLAKE_TIMESTAMP_TZ_OFFSET_SEPARATOR, "$1$2");
}

export function parseCustomAnalyticsReportFailureCode(
  value: string | null | undefined,
): CustomAnalyticsReportFailureCode | null {
  const parsed = CustomAnalyticsReportFailureCodeSchema.safeParse(
    value?.trim(),
  );
  return parsed.success ? parsed.data : null;
}

export function parseCustomAnalyticsReportFailureReason(
  value: string | null | undefined,
): CustomAnalyticsReportFailureReason | null {
  const parsed = CustomAnalyticsReportFailureReasonSchema.safeParse(
    value?.trim(),
  );
  return parsed.success ? parsed.data : null;
}

export function toCustomAnalyticsReportFailureCode(
  value: string | null | undefined,
  fallback: CustomAnalyticsReportFailureCode = CustomAnalyticsReportFailureCode.InternalError,
): CustomAnalyticsReportFailureCode {
  const trimmed = value?.trim();
  const parsed = parseCustomAnalyticsReportFailureCode(trimmed);
  if (parsed) {
    return parsed;
  }

  if (
    !trimmed ||
    trimmed.length > 500 ||
    CUSTOM_ANALYTICS_REPORT_INTERNAL_ERROR_PATTERNS.some((pattern) =>
      pattern.test(trimmed),
    )
  ) {
    return CustomAnalyticsReportFailureCode.InternalError;
  }

  return fallback;
}

export function getCustomAnalyticsReportUserFacingErrorMessage(
  errorMessage: string | null | undefined,
): string | null {
  const trimmed = errorMessage?.trim();
  if (!trimmed) {
    return null;
  }

  const failureCode = parseCustomAnalyticsReportFailureCode(trimmed);
  if (failureCode) {
    return CUSTOM_ANALYTICS_REPORT_FAILURE_MESSAGES[failureCode];
  }

  if (
    trimmed.length > 500 ||
    CUSTOM_ANALYTICS_REPORT_INTERNAL_ERROR_PATTERNS.some((pattern) =>
      pattern.test(trimmed),
    )
  ) {
    return CUSTOM_ANALYTICS_REPORT_INTERNAL_ERROR_MESSAGE;
  }

  return trimmed;
}

export function buildCustomAnalyticsReportColumns(
  sourceColumns: CustomAnalyticsReportSourceColumn[],
  options: BuildCustomAnalyticsReportColumnsOptions = {},
): CustomAnalyticsReportColumn[] {
  const columnKeys = buildCustomAnalyticsReportColumnKeys(
    sourceColumns.map(({ name }) => name),
  );
  const seenSourceNames = new Set<string>();
  const presentationBySourceName = buildColumnPresentationBySourceName(
    options.columnPresentation,
  );
  const helpBySourceName = buildColumnHelpBySourceName(options.columnHelp);
  const defaultBySourceName = new Map(
    (options.columnDefaults ?? []).map((columnDefault) => [
      columnDefault.sourceName.trim().toUpperCase(),
      columnDefault,
    ]),
  );

  return sourceColumns.map((sourceColumn, index) => {
    const sourceNameKey = sourceColumn.name.toUpperCase();
    if (seenSourceNames.has(sourceNameKey)) {
      throw new Error(
        `Custom analytics report has duplicate column name "${sourceColumn.name}".`,
      );
    }
    seenSourceNames.add(sourceNameKey);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const key = columnKeys[index]!;
    const inferredKind = inferCustomAnalyticsReportColumnKind(sourceColumn);
    const presentation = presentationBySourceName.get(sourceNameKey);
    const columnDefault = defaultBySourceName.get(sourceNameKey);
    const help = helpBySourceName.get(sourceNameKey);
    const kind = chooseCustomAnalyticsReportColumnKind({
      sourceColumn,
      inferredKind,
      proposedKind: presentation?.kind ?? columnDefault?.kind ?? null,
    });
    const format = buildCustomAnalyticsReportColumnFormat({
      kind,
      decimalPlaces: presentation?.format?.decimalPlaces ?? null,
    });

    const baseColumn = {
      key,
      sourceName: sourceColumn.name,
      label:
        normalizeCustomAnalyticsReportColumnLabel({
          label: presentation?.label,
          sourceName: sourceColumn.name,
        }) ??
        normalizeCustomAnalyticsReportColumnLabel({
          label: columnDefault?.label,
          sourceName: sourceColumn.name,
        }) ??
        labelFromCustomAnalyticsReportColumnName(sourceColumn.name),
      sqlType: sourceColumn.type,
      order: index,
      align: "left" as const,
      ...(help ? { help } : {}),
    };

    if (kind === "number" || kind === "percent") {
      return { ...baseColumn, kind, ...(format ? { format } : {}) };
    }

    return { ...baseColumn, kind };
  });
}

function buildColumnHelpBySourceName(
  columnHelp:
    | readonly CustomAnalyticsReportColumnHelpInput[]
    | null
    | undefined,
): ReadonlyMap<string, CustomAnalyticsReportColumnHelp> {
  const helpBySourceName = new Map<string, CustomAnalyticsReportColumnHelp>();

  for (const column of columnHelp ?? []) {
    const sourceName = column.sourceName.trim();
    if (!sourceName) {
      continue;
    }

    const sourceNameKey = sourceName.toUpperCase();
    if (!helpBySourceName.has(sourceNameKey)) {
      helpBySourceName.set(sourceNameKey, column.help);
    }
  }

  return helpBySourceName;
}

export function buildCustomAnalyticsReportColumnPresentationFromColumns(
  columns: readonly CustomAnalyticsReportColumn[],
): CustomAnalyticsReportColumnPresentation[] {
  return columns.map((column) => {
    const baseColumn = { sourceName: column.sourceName, label: column.label };

    if (column.kind === "number" || column.kind === "percent") {
      return {
        ...baseColumn,
        kind: column.kind,
        format: column.format ?? null,
      };
    }

    return { ...baseColumn, kind: column.kind, format: null };
  });
}

function buildColumnPresentationBySourceName(
  columnPresentation:
    | readonly CustomAnalyticsReportColumnPresentation[]
    | null
    | undefined,
): ReadonlyMap<string, CustomAnalyticsReportColumnPresentation> {
  const presentationBySourceName = new Map<
    string,
    CustomAnalyticsReportColumnPresentation
  >();

  for (const presentation of columnPresentation ?? []) {
    const sourceName = presentation.sourceName.trim();
    if (!sourceName) {
      continue;
    }

    const sourceNameKey = sourceName.toUpperCase();
    if (!presentationBySourceName.has(sourceNameKey)) {
      presentationBySourceName.set(sourceNameKey, presentation);
    }
  }

  return presentationBySourceName;
}

function normalizeCustomAnalyticsReportColumnLabel({
  label,
  sourceName,
}: {
  label: string | null | undefined;
  sourceName: string;
}): string | null {
  // Model-generated labels can contain newlines or repeated spaces; collapse
  // them before applying the UI length cap.
  const normalized = label?.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return null;
  }

  const normalizedSourceName = sourceName.trim();
  if (
    normalized.toUpperCase() === normalizedSourceName.toUpperCase() &&
    isMachineGeneratedColumnName(normalizedSourceName)
  ) {
    return null;
  }

  return normalized.slice(0, CUSTOM_ANALYTICS_REPORT_COLUMN_LABEL_MAX_LENGTH);
}

function isMachineGeneratedColumnName(sourceName: string): boolean {
  return (
    MACHINE_GENERATED_COLUMN_NAME_PATTERN.test(sourceName) &&
    (sourceName.includes("_") ||
      CAMEL_CASE_COLUMN_NAME_PATTERN.test(sourceName) ||
      sourceName === sourceName.toLowerCase())
  );
}

function chooseCustomAnalyticsReportColumnKind({
  sourceColumn,
  inferredKind,
  proposedKind,
}: {
  sourceColumn: CustomAnalyticsReportSourceColumn;
  inferredKind: CustomAnalyticsReportColumnKind;
  proposedKind: CustomAnalyticsReportColumnKind | null;
}): CustomAnalyticsReportColumnKind {
  if (!proposedKind || proposedKind === inferredKind) {
    return inferredKind;
  }

  const type = sourceColumn.type.toUpperCase();
  const name = sourceColumn.name.toLowerCase();

  if (isNumericSnowflakeType(type)) {
    return chooseNumericCustomAnalyticsReportColumnKind({
      inferredKind,
      proposedKind,
    });
  }

  if (type.includes("TIMESTAMP") || name.endsWith("_at")) {
    return proposedKind === "datetime" || proposedKind === "date"
      ? proposedKind
      : inferredKind;
  }

  if (type === "DATE" || name.endsWith("_date") || name.endsWith("_day")) {
    return proposedKind === "date" ? proposedKind : inferredKind;
  }

  if (type === "BOOLEAN") {
    return proposedKind === "boolean" ? proposedKind : inferredKind;
  }

  if (isJsonSnowflakeType(type)) {
    return proposedKind === "json" ? proposedKind : inferredKind;
  }

  return proposedKind === "text" ? proposedKind : inferredKind;
}

function chooseNumericCustomAnalyticsReportColumnKind({
  inferredKind,
  proposedKind,
}: {
  inferredKind: CustomAnalyticsReportColumnKind;
  proposedKind: CustomAnalyticsReportColumnKind;
}): CustomAnalyticsReportColumnKind {
  if (!NUMERIC_COLUMN_KINDS.has(proposedKind)) {
    return inferredKind;
  }

  if (inferredKind === "number") {
    return proposedKind;
  }

  if (inferredKind === "integer") {
    return proposedKind === "number" ? proposedKind : inferredKind;
  }

  return proposedKind === inferredKind ? proposedKind : inferredKind;
}

function buildCustomAnalyticsReportColumnFormat({
  kind,
  decimalPlaces,
}: {
  kind: CustomAnalyticsReportColumnKind;
  decimalPlaces: number | null;
}): CustomAnalyticsReportColumnFormat | null {
  if (kind === "percent") {
    return {
      decimalPlaces: Math.min(
        decimalPlaces ?? CUSTOM_ANALYTICS_REPORT_DEFAULT_PERCENT_DECIMAL_PLACES,
        CUSTOM_ANALYTICS_REPORT_DEFAULT_PERCENT_DECIMAL_PLACES,
      ),
    };
  }

  if (decimalPlaces === null || kind !== "number") {
    return null;
  }

  return { decimalPlaces };
}

export function buildCustomAnalyticsReportColumnKeys(
  sourceNames: readonly string[],
): string[] {
  const usedKeys = new Map<string, number>();

  return sourceNames.map((sourceName) => {
    const keyBase = normalizeCustomAnalyticsReportColumnKey(sourceName);
    const keyCount = usedKeys.get(keyBase) ?? 0;
    usedKeys.set(keyBase, keyCount + 1);
    return keyCount === 0 ? keyBase : `${keyBase}${keyCount + 1}`;
  });
}

function normalizeCustomAnalyticsReportColumnKey(sourceName: string): string {
  const words = splitCustomAnalyticsReportColumnName(sourceName);
  if (words.length === 0) {
    return "column";
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const firstWord = words[0]!;
  const restWords = words.slice(1);
  const normalized =
    firstWord.toLowerCase() +
    restWords.map((word) => capitalizeColumnWord(word)).join("");

  return startsWithAsciiLetter(normalized) ? normalized : `column${normalized}`;
}

function labelFromCustomAnalyticsReportColumnName(sourceName: string): string {
  const words = splitCustomAnalyticsReportColumnName(sourceName);
  if (words.length === 0) {
    return "Column";
  }

  return words
    .map((word) => {
      const lowerWord = word.toLowerCase();
      return isKnownColumnAcronym(lowerWord)
        ? lowerWord.toUpperCase()
        : capitalizeColumnWord(lowerWord);
    })
    .join(" ");
}

function splitCustomAnalyticsReportColumnName(sourceName: string): string[] {
  /*
   * Snowflake usually gives us SCREAMING_SNAKE_CASE column names, while quoted
   * SQL aliases can be camelCase, spaced, or punctuation-separated. Normalize
   * all of those into plain alphanumeric words so the internal row key and
   * visible table label are derived from the same source.
   */
  return sourceName
    .trim()
    .replace(CAMEL_CASE_WORD_BOUNDARY, "$1 $2")
    .split(COLUMN_WORD_SEPARATOR)
    .filter(Boolean);
}

function capitalizeColumnWord(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function startsWithAsciiLetter(value: string): boolean {
  const firstCharCode = value.charCodeAt(0);
  return (
    (firstCharCode >= ASCII_UPPERCASE_A_CHAR_CODE &&
      firstCharCode <= ASCII_UPPERCASE_Z_CHAR_CODE) ||
    (firstCharCode >= ASCII_LOWERCASE_A_CHAR_CODE &&
      firstCharCode <= ASCII_LOWERCASE_Z_CHAR_CODE)
  );
}

function isKnownColumnAcronym(word: string): boolean {
  return ["ai", "id", "sku", "upc", "rma", "url"].includes(word);
}

function inferCustomAnalyticsReportColumnKind(
  sourceColumn: CustomAnalyticsReportSourceColumn,
): CustomAnalyticsReportColumnKind {
  const type = sourceColumn.type.toUpperCase();
  const name = sourceColumn.name.toLowerCase();

  if (type.includes("TIMESTAMP")) {
    return "datetime";
  }
  if (type === "DATE" || name.endsWith("_date") || name.endsWith("_day")) {
    return "date";
  }
  if (type === "BOOLEAN") {
    return "boolean";
  }
  if (isJsonSnowflakeType(type)) {
    return "json";
  }
  if (name.endsWith("_at")) {
    return "datetime";
  }
  if (name.endsWith("_month")) {
    return "date";
  }
  if (isNumericSnowflakeType(type)) {
    if (isPercentColumnName(name)) {
      return "percent";
    }
    if (isIntegerColumnName(name)) {
      return "integer";
    }
    if (isCurrencyColumnName(name)) {
      return "currency";
    }
    if (sourceColumn.scale === 0) {
      return "integer";
    }
    return "number";
  }

  return "text";
}

function isNumericSnowflakeType(type: string): boolean {
  return (
    type === "NUMBER" ||
    type === "FIXED" ||
    type === "DECIMAL" ||
    type === "NUMERIC" ||
    type === "INT" ||
    type === "INTEGER" ||
    type === "BIGINT" ||
    type === "FLOAT" ||
    type === "DOUBLE" ||
    type === "REAL"
  );
}

function isJsonSnowflakeType(type: string): boolean {
  return (
    type === "VARIANT" ||
    type === "OBJECT" ||
    type === "ARRAY" ||
    type === "MAP"
  );
}

function isCurrencyColumnName(name: string): boolean {
  return hasCustomAnalyticsReportColumnNameWord(
    name,
    CURRENCY_COLUMN_NAME_WORDS,
  );
}

function isPercentColumnName(name: string): boolean {
  return hasCustomAnalyticsReportColumnNameWord(
    name,
    PERCENT_COLUMN_NAME_WORDS,
  );
}

function isIntegerColumnName(name: string): boolean {
  return hasCustomAnalyticsReportColumnNameWord(
    name,
    INTEGER_COLUMN_NAME_WORDS,
  );
}

function hasCustomAnalyticsReportColumnNameWord(
  name: string,
  words: ReadonlySet<string>,
): boolean {
  return splitCustomAnalyticsReportColumnName(name).some((word) =>
    words.has(word.toLowerCase()),
  );
}

export const CUSTOM_ANALYTICS_REPORT_HARD_VISIBLE_COLUMN_LIMIT = 25;
